import React, { useEffect } from 'react';
import { UnscheduledEvent } from '../../types';
import { EVENT_STATUS } from '../../constants';
import { getFlightAutoPopulate } from '../../services/flightService';
import { epochSecondsToZulu } from './dateTimeUtils';
import { buildOpeningSnapshot } from './openingSnapshot';

// How often the open modal re-checks for the current next scheduled flight.
// The backend refreshes tail-mapping every ~10 min, so this comfortably tracks
// it without hammering the API (a light DynamoDB-backed read per tick).
const REFRESH_INTERVAL_MS = 60_000;

/**
 * Keeps the "Next Scheduled Flight" fields live for an existing event.
 *
 * When the modal opens for a saved event, and then on a recurring interval while
 * it stays open, this refreshes `nextScheduledFlightNumber` /
 * `nextScheduledFlightTimeZulu` to the CURRENT next scheduled flight from the
 * tail-mapping data (pivot = now), so they track later schedule changes and tail
 * reassignments without reopening. The Previous Flight and Chargeable Flight
 * fields are intentionally left untouched — frozen at creation.
 *
 * The opening snapshot is patched with the same values so this passive refresh
 * never flags the record as edited. Skipped for Scheduled/Deferred records
 * (no Next Scheduled field), IROP records (RTB/ATO/ATB/DIV — Next is frozen),
 * on create (no eventId). The Next Scheduled Flight fields are system-managed
 * (read-only), so there is no manual override to preserve.
 *
 * @param isOpen Whether the modal is open.
 * @param event The event being viewed/edited (null on create).
 * @param setFormData Setter for the modal's form state.
 * @param openingSnapshotRef Ref holding the serialized opening snapshot.
 */
/**
 * Whether the live Next Scheduled refresh should be skipped for this event.
 * Skipped when there is no tail; for Scheduled/Deferred (no Next Scheduled
 * field); for IROP records (RTB/ATO/ATB/DIV — Next is the returned flight); and
 * for Completed records (the Next Scheduled Flight is frozen once completed).
 */
function skipLiveNextFlight(event: UnscheduledEvent): boolean {
  const type = event.recordType || 'Unscheduled';
  const isIrop = Boolean(
    event.returnToBlock || event.abortedTakeOff || event.airTurnBack || event.diversion,
  );
  return (
    !event.tailNumber ||
    type === 'Scheduled' ||
    type === 'Deferred' ||
    isIrop ||
    event.status === EVENT_STATUS.COMPLETED
  );
}

export function useLiveNextFlight(
  isOpen: boolean,
  event: UnscheduledEvent | null,
  setFormData: React.Dispatch<React.SetStateAction<UnscheduledEvent | null>>,
  openingSnapshotRef: React.MutableRefObject<string>,
): void {
  useEffect(() => {
    if (!isOpen || !event?.eventId || skipLiveNextFlight(event)) return undefined;
    const tail = event.tailNumber;

    let cancelled = false;

    const refresh = async (): Promise<void> => {
      try {
        const result = await getFlightAutoPopulate(tail);
        if (cancelled || !result) return;
        const flightNumber: string = String(result.nextFlight?.flightNumber || '').trim();
        const flightTimeZulu: string = epochSecondsToZulu(
          result.nextFlight?.scheduledDepartureTime,
        );
        const hasCompleteNextFlight: boolean = Boolean(flightNumber && flightTimeZulu);
        const nextScheduledFlightNumber: string = hasCompleteNextFlight ? flightNumber : '';
        const nextScheduledFlightTimeZulu: string = hasCompleteNextFlight ? flightTimeZulu : '';

        setFormData((previous) => {
          // The request may finish after the user changes tail/status/IROP.
          // Never apply that stale response to the newer draft.
          if (!previous || previous.tailNumber !== tail || skipLiveNextFlight(previous)) {
            return previous;
          }
          openingSnapshotRef.current = buildOpeningSnapshot({
            ...event,
            nextScheduledFlightNumber,
            nextScheduledFlightTimeZulu,
          });
          return { ...previous, nextScheduledFlightNumber, nextScheduledFlightTimeZulu };
        });
      } catch {
        // Transient read failure — keep the last known value and try next tick.
      }
    };

    void refresh();
    const timer = setInterval(() => {
      void refresh();
    }, REFRESH_INTERVAL_MS);

    return (): void => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [isOpen, event, setFormData, openingSnapshotRef]);
}
